import { compileTimeline, requiredAudio, secondsPerTick, songDurationSeconds, type ScheduledClip, type ScheduledNote } from '../music/compile';
import type { Project } from '../domain/schemas';
import { AssetLoadError } from './assets/bufferCache';
import { buildGraph, type AudioSource, type Graph } from './graph';
import { encodeWav, levelStats } from './wav';

export const EXPORT_SAMPLE_RATE = 44100;
export const MAX_EXPORT_SECONDS = 300;
export const RELEASE_TAIL_SECONDS = 2;

export type ExportErrorCode = 'empty' | 'too-long' | 'render-failed' | 'missing-asset';

export class ExportError extends Error {
  readonly code: ExportErrorCode;
  constructor(code: ExportErrorCode, message: string) {
    super(message);
    this.name = 'ExportError';
    this.code = code;
  }
}

export interface RenderResult {
  channels: Float32Array[];
  sampleRate: number;
  /** Length of the arrangement itself (without the release tail). */
  songSeconds: number;
  durationSec: number;
  peak: number;
  rms: number;
  /** Gain applied after rendering to keep peaks below full scale (0 when none was needed). */
  normalizedDb: number;
  /** Notes the synthesis engine refused to schedule (expected to be 0). */
  droppedNotes: number;
  /** Non-fatal failures while disconnecting finished voices during the render. */
  cleanupErrors: number;
  /** Wall-clock time spent rendering. */
  renderMs: number;
}

const formatTime = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;

/** Throws an ExportError when the project's arrangement cannot be exported. */
export function checkExportable(project: Project): number {
  if (project.arrangement.sections.length === 0) {
    throw new ExportError('empty', 'The arrangement is empty. Add at least one section before exporting.');
  }
  const seconds = songDurationSeconds(project);
  if (seconds > MAX_EXPORT_SECONDS) {
    throw new ExportError(
      'too-long',
      `This song is ${formatTime(seconds)} long; WAV export is limited to ${formatTime(MAX_EXPORT_SECONDS)}. Shorten the arrangement or raise the tempo.`,
    );
  }
  const timeline = compileTimeline(project, { mode: 'song' });
  if (timeline.events.length === 0 && timeline.clips.length === 0) {
    throw new ExportError('empty', 'The arrangement has no notes or audio clips to render.');
  }
  return seconds;
}

export type OfflineContextFactory = (channels: number, frames: number, sampleRate: number) => OfflineAudioContext;

const nativeOfflineContext: OfflineContextFactory = (channels, frames, sampleRate) => new OfflineAudioContext(channels, frames, sampleRate);

/** Seconds of audio scheduled ahead of the render position (see renderSong). */
const SCHEDULE_AHEAD = 1;
const CHUNK = 0.5;

export interface RenderOptions {
  /**
   * Abort if rendering has not finished after this long (a stalled checkpoint, a resume
   * that never settles). Default: max(2 min, 3 × the audio length).
   */
  timeoutMs?: number;
  /** Test seam: wraps or replaces graph construction. */
  buildGraph?: typeof buildGraph;
  /**
   * Decoded samples and clips (the shared AudioBufferCache). Required when the song uses
   * any; everything needed is loaded and pinned before rendering starts.
   */
  audio?: RenderAudio;
}

export interface RenderAudio extends AudioSource {
  acquire(wanted: Iterable<{ assetId: string; reversed: boolean }>): Promise<{ release(): void }>;
}

/** Loads and pins every asset the song needs, or fails with a readable 'missing-asset' error. */
async function acquireAudio(project: Project, wanted: { assetId: string; reversed: boolean }[], audio: RenderAudio | undefined) {
  if (wanted.length === 0) return { release: () => undefined };
  const nameOf = (id: string) => project.assets.find((a) => a.id === id)?.name ?? id;
  if (!audio) throw new ExportError('missing-asset', `The song uses audio files (${nameOf(wanted[0]!.assetId)}) but no audio loader is available.`);
  try {
    return await audio.acquire(wanted);
  } catch (error) {
    if (error instanceof AssetLoadError) {
      throw new ExportError(
        'missing-asset',
        error.code === 'budget' ? error.message : `“${nameOf(error.assetId)}” cannot be used: ${error.message}`,
      );
    }
    throw new ExportError('missing-asset', `The song’s audio files could not be loaded: ${message(error)}`);
  }
}

/** "InvalidStateError: suspend time already passed" for DOMExceptions, plain message otherwise. */
function message(error: unknown): string {
  if (typeof error === 'object' && error !== null && typeof (error as { message?: unknown }).message === 'string') {
    const { name, message: text } = error as { name?: unknown; message: string };
    return typeof name === 'string' && name && name !== 'Error' ? `${name}: ${text}` : text;
  }
  return String(error);
}

/** A fixed view of the buffers a render needs (AudioBuffers are never modified once decoded). */
function holdBuffers(wanted: { assetId: string; reversed: boolean }[], audio: RenderAudio | undefined): AudioSource | undefined {
  if (!audio) return undefined;
  const buffers = new Map<string, AudioBuffer>();
  for (const w of wanted) {
    const b = audio.peek(w.assetId, w.reversed);
    if (b) buffers.set(`${w.assetId}:${w.reversed}`, b);
  }
  return { peek: (assetId, reversed) => buffers.get(`${assetId}:${reversed}`) };
}

function disposeQuietly(graph: Graph | null): void {
  if (!graph) return;
  try {
    graph.dispose();
  } catch (error) {
    // Cleanup must never mask the render result or the original failure.
    console.warn('SonicForge: could not fully dispose the offline audio graph', error);
  }
}

/**
 * What an offline render plays: notes and clips at output times (seconds), sorted by time.
 * `fromSec` starts a clip part-way through (a clip already sounding at a session's start).
 */
export interface RenderPlan {
  notes: { note: ScheduledNote; time: number }[];
  clips: { clip: ScheduledClip; time: number; fromSec?: number }[];
}

export interface PlanRenderOptions extends RenderOptions {
  /** Cancels the render (rejects with the signal's reason; the context is left suspended). */
  signal?: AbortSignal;
  /** Fraction of the audio rendered (0–1), reported at each checkpoint. */
  onProgress?: (fraction: number) => void;
}

/** A finished offline render: the rendered buffer (owned by the caller) and diagnostics. */
export interface PlanRenderResult {
  buffer: AudioBuffer;
  droppedNotes: number;
  cleanupErrors: number;
  renderMs: number;
}

/**
 * Renders the arrangement offline (faster than realtime, off the main thread) through
 * the same instrument graph and compiled event list used for live playback.
 *
 * Notes are scheduled just in time: rendering pauses every half second (`suspend`) and
 * the next second of notes is created then. Creating every node up front made render
 * time grow with the square of the note count.
 *
 * Failure contract — every checkpoint either resumes rendering or ends the export with
 * an ExportError('render-failed') that names the stage and time:
 *  - a suspend() that throws or rejects (that checkpoint would be skipped, so notes after
 *    it could be scheduled late or not at all) fails the export;
 *  - an exception while scheduling notes fails the export; the checkpoint still resumes
 *    so the offline context can run to completion instead of staying suspended;
 *  - a resume() that throws or rejects fails the export;
 *  - an exception while disconnecting finished voices (`collect`) is not fatal: it only
 *    affects memory, so it is counted in `cleanupErrors` and rendering continues;
 *  - a render that stalls past `timeoutMs` fails the export;
 *  - a render that ends before every note was scheduled fails the export.
 * The graph is disposed on every path.
 */
export async function renderSong(
  project: Project,
  createContext: OfflineContextFactory = nativeOfflineContext,
  sampleRate = EXPORT_SAMPLE_RATE,
  options: RenderOptions = {},
): Promise<RenderResult> {
  const songSeconds = checkExportable(project);
  const timeline = compileTimeline(project, { mode: 'song' });
  const spt = secondsPerTick(project.settings.bpm);
  const plan: RenderPlan = {
    notes: timeline.events.map((note) => ({ note, time: note.tick * spt })),
    clips: timeline.clips.map((clip) => ({ clip, time: clip.tick * spt })),
  };
  const durationSec = songSeconds + RELEASE_TAIL_SECONDS;
  const rendered = await renderPlan(project, plan, requiredAudio(project, timeline), Math.ceil(durationSec * sampleRate), createContext, sampleRate, options);
  const audio = rendered.buffer;
  const channels = Array.from({ length: audio.numberOfChannels }, (_, i) => new Float32Array(audio.getChannelData(i)));
  const { peak, rms, normalizedDb } = normalizeChannels(channels);
  return {
    channels,
    sampleRate: audio.sampleRate,
    songSeconds,
    durationSec: channels[0]!.length / audio.sampleRate,
    peak,
    rms,
    normalizedDb,
    droppedNotes: rendered.droppedNotes,
    cleanupErrors: rendered.cleanupErrors,
    renderMs: rendered.renderMs,
  };
}

/**
 * Clipping protection (in place): the limiter and soft clipper should already hold peaks
 * below full scale; if anything still exceeds −0.3 dBFS, the whole render is scaled down.
 */
export function normalizeChannels(channels: readonly Float32Array[]): { peak: number; rms: number; normalizedDb: number } {
  let { peak, rms } = levelStats(channels);
  let normalizedDb = 0;
  const ceiling = 0.966;
  if (peak > ceiling) {
    const gain = ceiling / peak;
    for (const c of channels) for (let i = 0; i < c.length; i++) c[i]! *= gain;
    normalizedDb = Math.round(20 * Math.log10(gain) * 10) / 10;
    peak *= gain;
    rms *= gain;
  }
  return { peak, rms, normalizedDb };
}

/**
 * Renders a plan of `frames` samples offline (the WAV export and the Phase 4B session
 * audio share this). Follows the failure contract described on `renderSong`; on abort, the
 * render stops at the next checkpoint (the context is never resumed again) and the
 * promise rejects with the signal's reason.
 */
export async function renderPlan(
  project: Project,
  plan: RenderPlan,
  wanted: { assetId: string; reversed: boolean }[],
  frames: number,
  createContext: OfflineContextFactory = nativeOfflineContext,
  sampleRate = EXPORT_SAMPLE_RATE,
  options: PlanRenderOptions = {},
): Promise<PlanRenderResult> {
  const durationSec = frames / sampleRate;
  const timeoutMs = options.timeoutMs ?? Math.max(120_000, durationSec * 3000);
  const makeGraph = options.buildGraph ?? buildGraph;
  const signal = options.signal;
  const started = performance.now();
  let graph: Graph | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cleanupErrors = 0;
  signal?.throwIfAborted();
  // Before any audio context exists: a missing or undecodable file fails fast.
  const pinned = await acquireAudio(project, wanted, options.audio);
  // The decoded buffers are resolved once, now, and held for the whole render: deleting or
  // re-importing a file while rendering (which drops it from the shared cache) cannot
  // change or break a render that has started.
  const held = holdBuffers(wanted, options.audio);
  let onAbort: (() => void) | null = null;
  try {
    signal?.throwIfAborted();
    const ctx = createContext(2, frames, sampleRate);
    const g = makeGraph(ctx, ctx.destination, project.tracks, project.settings.masterVolumeDb, project.settings.bpm, {
      meter: false,
      audio: held,
      bus: project.audioBus,
    });
    graph = g;
    const { notes, clips } = plan;
    let next = 0;
    let nextClip = 0;
    const scheduleUntil = (until: number) => {
      while (next < notes.length && notes[next]!.time < until) {
        const n = notes[next++]!;
        g.trigger(n.note, n.time);
      }
      // Clips start just in time too; their sources are created when they are reached.
      while (nextClip < clips.length && clips[nextClip]!.time < until) {
        const c = clips[nextClip++]!;
        g.playClip(c.clip, c.time, c.fromSec);
      }
      if (next >= notes.length) g.flush();
    };

    // The first failure wins; it rejects `aborted`, which races the render below.
    let failure: Error | null = null;
    let abort!: (error: Error) => void;
    const aborted = new Promise<never>((_, reject) => {
      abort = reject;
    });
    aborted.catch(() => undefined); // observed via the race; never an unhandled rejection
    const fail = (stage: string, at: number, error: unknown) => {
      if (failure) return;
      failure = new ExportError('render-failed', `Rendering stopped at ${at.toFixed(1)} s while ${stage}: ${message(error)}`);
      abort(failure);
    };
    if (signal) {
      onAbort = () => {
        if (failure) return;
        failure = signal.reason instanceof Error ? signal.reason : new DOMException('The render was cancelled.', 'AbortError');
        abort(failure);
      };
      signal.addEventListener('abort', onAbort);
    }

    if (typeof ctx.suspend === 'function') {
      // Schedules before rendering starts propagate directly to the outer catch.
      scheduleUntil(SCHEDULE_AHEAD);
      for (let t = CHUNK; t < durationSec - 0.01; t += CHUNK) {
        const at = t;
        let checkpoint: Promise<void>;
        try {
          checkpoint = ctx.suspend(at);
        } catch (error) {
          fail('setting up a render checkpoint', at, error);
          break;
        }
        checkpoint.then(
          async () => {
            // Cancelled: stay suspended (the context is dropped, no more CPU is spent).
            if (signal?.aborted) return;
            options.onProgress?.(at / durationSec);
            if (!failure) {
              try {
                scheduleUntil(at + SCHEDULE_AHEAD);
              } catch (error) {
                fail('scheduling notes', at, error);
              }
            }
            if (!failure) {
              try {
                g.collect(at);
              } catch {
                cleanupErrors++;
              }
            }
            // Always resume — after a failure too — so the offline context finishes
            // rather than staying suspended forever.
            try {
              await ctx.resume();
            } catch (error) {
              fail('resuming the render', at, error);
            }
          },
          (error: unknown) => fail('waiting for a render checkpoint', at, error),
        );
      }
    } else {
      scheduleUntil(Infinity); // Older engines without offline suspend: schedule everything.
    }

    const rendering = ctx.startRendering();
    rendering.catch(() => undefined); // may settle after an abort; its outcome is then irrelevant
    const timedOut = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new ExportError('render-failed', `Rendering did not finish within ${(timeoutMs / 1000).toFixed(timeoutMs < 10_000 ? 1 : 0)} s and was abandoned.`)),
        timeoutMs,
      );
    });
    const buffer = await Promise.race([rendering, aborted, timedOut]);
    if (failure) throw failure;
    if (next < notes.length || nextClip < clips.length) {
      throw new ExportError(
        'render-failed',
        `Rendering finished before everything was scheduled (${next} of ${notes.length} notes, ${nextClip} of ${clips.length} clips).`,
      );
    }
    if (g.missingAssets.size > 0) {
      const names = [...g.missingAssets].map((id) => project.assets.find((a) => a.id === id)?.name ?? id);
      throw new ExportError('missing-asset', `Audio for ${names.join(', ')} was unavailable during rendering.`);
    }
    options.onProgress?.(1);
    return { buffer, droppedNotes: g.droppedNotes, cleanupErrors, renderMs: Math.round(performance.now() - started) };
  } catch (error) {
    if (error instanceof ExportError) throw error;
    if (signal?.aborted && (error === signal.reason || (error instanceof DOMException && error.name === 'AbortError'))) throw error;
    throw new ExportError('render-failed', `Rendering failed: ${message(error)}`);
  } finally {
    clearTimeout(timer);
    if (onAbort) signal?.removeEventListener('abort', onAbort);
    disposeQuietly(graph);
    pinned.release();
  }
}

export function renderToWav(result: RenderResult): ArrayBuffer {
  return encodeWav(result.channels, result.sampleRate);
}

export function wavFileName(project: Project): string {
  const slug = project.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'song';
  return `${slug}.wav`;
}
